'use client';

import Link from 'next/link';

import { AuthLoading } from '@/components/auth/auth-loading';
import { useAuthGuard } from '@/components/ui/auth-guard';
import { Button } from '@/components/ui/button';

import {
  BarChart3,
  CheckCircle2,
  Crown,
  Lightbulb,
  LineChart,
  Lock
} from 'lucide-react';

interface PremiumSectionProps {
  children: React.ReactNode;
  title?: string;
  description?: string;
  showPaywall?: boolean;
  className?: string;
}

export function PremiumSection({
  children,
  title = 'Premium Content',
  description = 'Subscribe to continue reading this deep dive and unlock full access.',
  showPaywall = true,
  className = ''
}: PremiumSectionProps) {
  const { isAuthenticated, hasActiveSubscription, loading, subscription } =
    useAuthGuard();

  if (loading) {
    return (
      <div
        className={`border-border my-8 rounded-2xl border border-dashed p-12 ${className}`}>
        <AuthLoading message='Verifying access...' />
      </div>
    );
  }

  // User has active subscription - show content
  if (isAuthenticated && hasActiveSubscription) {
    return (
      <div className={`premium-content ${className}`}>
        <div className='bg-primary/10 border-primary/20 mb-8 flex items-center justify-center gap-2 rounded-xl border p-4'>
          <Crown className='text-primary size-5' />
          <span className='text-primary text-sm font-bold tracking-wider uppercase'>
            Premium Subscriber Access
          </span>
        </div>
        {children}
      </div>
    );
  }

  if (!showPaywall) return null;

  const features = [
    {
      icon: <BarChart3 className='size-5' />,
      text: 'Interactive data visualizations'
    },
    {
      icon: <CheckCircle2 className='size-5' />,
      text: 'Detailed sector analysis'
    },
    { icon: <LineChart className='size-5' />, text: 'Investment predictions' },
    { icon: <Lightbulb className='size-5' />, text: 'Actionable insights' }
  ];

  return (
    <div
      className={`border-border bg-card/60 relative my-16 overflow-hidden rounded-2xl border shadow-2xl backdrop-blur-xl ${className}`}>
      {/* Subtle gradient glow in the background */}
      <div className='from-primary/10 pointer-events-none absolute inset-x-0 top-0 h-40 bg-linear-to-b to-transparent' />

      <div className='relative p-8 text-center md:p-12'>
        <div className='bg-background border-border group relative mb-6 inline-flex size-20 items-center justify-center rounded-full border shadow-inner'>
          <div className='bg-primary/20 absolute inset-0 scale-75 rounded-full blur-xl transition-transform duration-700' />
          <Lock className='text-primary relative z-10 size-8' />
        </div>

        <h3 className='text-foreground mb-4 font-serif text-3xl font-bold md:text-4xl'>
          {title}
        </h3>
        <p className='text-muted-foreground mx-auto mb-10 max-w-lg text-lg'>
          {description}
        </p>

        {/* Feature Grid */}
        <div className='mx-auto mb-10 grid max-w-2xl grid-cols-1 gap-4 text-left sm:grid-cols-2'>
          {features.map((feature, i) => (
            <div
              key={i}
              className='bg-background/50 border-border hover:border-primary/50 flex items-center gap-4 rounded-xl border p-4 transition-colors'>
              <div className='text-primary'>{feature.icon}</div>
              <span className='text-foreground font-medium'>
                {feature.text}
              </span>
            </div>
          ))}
        </div>

        <div className='mx-auto max-w-md space-y-4'>
          {isAuthenticated ? (
            <div className='space-y-4'>
              <Link
                href='/pricing'
                className='block w-full'>
                <Button className='bg-primary hover:bg-primary/90 text-primary-foreground shadow-primary/25 h-14 w-full text-lg shadow-lg'>
                  <Crown className='mr-2 size-5' />
                  Subscribe for $9.99/month
                </Button>
              </Link>
              <p className='text-muted-foreground text-sm font-medium'>
                Cancel anytime • 7-day free trial
              </p>
            </div>
          ) : (
            <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
              <Link
                href={`/auth/signin`}
                className='block w-full'>
                <Button className='bg-primary hover:bg-primary/90 text-primary-foreground h-12 w-full text-base'>
                  Sign In to Access
                </Button>
              </Link>
              <Link
                href='/auth/signup'
                className='block w-full'>
                <Button
                  variant='outline'
                  className='bg-background/50 border-border hover:bg-accent hover:text-accent-foreground h-12 w-full text-base'>
                  Create Free Account
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Hook for checking premium access in components
export function usePremiumAccess() {
  const { isAuthenticated, hasActiveSubscription, loading, user } =
    useAuthGuard();

  return {
    canAccessPremium: isAuthenticated && hasActiveSubscription,
    isAuthenticated,
    hasActiveSubscription,
    loading,
    user,
    needsUpgrade: isAuthenticated && !hasActiveSubscription,
    needsSignIn: !isAuthenticated
  };
}

// Premium feature toggle component
export function PremiumFeature({
  children,
  fallback,
  feature = 'feature'
}: {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  feature?: string;
}) {
  const { canAccessPremium, loading } = usePremiumAccess();

  if (loading) {
    return <div className='bg-muted h-8 animate-pulse rounded'></div>;
  }

  if (canAccessPremium) {
    return <>{children}</>;
  }

  if (fallback) {
    return <>{fallback}</>;
  }

  return (
    <span className='inline-flex items-center gap-1 rounded bg-amber-100 px-2 py-1 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-200'>
      <Lock className='size-3' />
      Premium {feature}
    </span>
  );
}
